import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Copy, Download, FileText } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Reveal, useSeo } from "../lib/ui";
import { VerifyChip } from "../components/DataLabel";
import { NotAValuation } from "../components/LegalNotices";
import { StartAgain } from "../components/StartAgain";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const ksh = (n) => (n ? `KSh ${Math.round(n).toLocaleString()}` : "Nothing");
const TERMS = [["lease_99", "99-year lease"], ["lease_50", "50-year lease"], ["lease_15", "15-year lease"],
  ["freehold", "Freehold purchase"], ["revenue_share", "Revenue share"], ["donation", "Donated or granted"]];
const LOCATIONS = [["cbd", "City centre or CBD"], ["urban", "Urban, inside the town"],
  ["peri_urban", "Peri-urban, edge of town"], ["satellite", "Satellite town or growth corridor"],
  ["rural", "Rural or agricultural"]];

/** One plot in, one indicative development scenario out. Not a valuation and not an agency service. */
const ACRE_M2 = 4046.86;

export default function SiteAppraisal() {
  const [params, setParams] = useSearchParams();
  // A shared link opens exactly the scenario that was sent: county, plot, location, facility and land route.
  // An acreage is accepted too, and squared off so the plot can still be drawn.
  const fromAcres = parseFloat(params.get("acres") || "");
  const side = fromAcres > 0 ? String(Math.round(Math.sqrt(fromAcres * ACRE_M2) * 10) / 10) : "";
  const [counties, setCounties] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [county, setCounty] = useState(params.get("county") || "Kisumu");
  const [length, setLength] = useState(params.get("plot_length_m") || side || "180");
  const [width, setWidth] = useState(params.get("plot_width_m") || side || "110");
  const [location, setLocation] = useState(params.get("location") || "peri_urban");
  const [facility, setFacility] = useState(params.get("facility") || "branded_five");
  const [term, setTerm] = useState(params.get("term") || "lease_99");
  const [d, setD] = useState(null);

  useSeo({
    title: "Indicative Site Development Scenario — KickStartKenyaFootball",
    description:
      "An indicative development scenario for any plot in Kenya: the land on every route, what pitches fit drawn to scale, an indicative build cost and what it could earn. Not a valuation, estate-agency service, survey, title investigation or investment recommendation.",
  });

  useEffect(() => {
    api.get("/counties").then((r) => {
      const rows = Array.isArray(r.data) ? r.data : r.data.counties || [];
      setCounties(rows.map((c) => c.county || c.name).filter(Boolean));
    }).catch(() => {});
    api.get("/facility-library")
      .then((r) => setFacilities(r.data.facilities || r.data.types || []))
      .catch(() => setFacilities([]));
  }, []);

  const query = new URLSearchParams({
    county, location, facility, term,
    plot_length_m: String(parseFloat(length || "0") || 0),
    plot_width_m: String(parseFloat(width || "0") || 0),
  }).toString();

  useEffect(() => {
    api.get(`/site-appraisal?${query}`).then((r) => setD(r.data)).catch(() => setD(null));
  }, [query]);

  // Keep the address bar holding the scenario, so the page can be shared or bookmarked as it stands.
  useEffect(() => {
    setParams(new URLSearchParams(query), { replace: true });
  }, [query, setParams]);

  const copyLink = async () => {
    const url = `${window.location.origin}/site-appraisal?${query}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied — it opens this exact scenario.");
    } catch {
      toast.message(url);
    }
  };

  if (!d) return <div className="min-h-[60vh] bg-black" data-testid="site-appraisal-loading" />;

  const { plot, plan, value, land, trading, rent_test: rent } = d;
  const scale = Math.min(900 / Math.max(plot.width_m, 1), 520 / Math.max(plot.length_m, 1));
  const input = "mt-2 w-28 border border-white/20 bg-transparent px-3 py-2.5 text-sm text-white focus:border-ochre focus:outline-none";
  const select = "mt-2 border border-white/20 bg-black px-3.5 py-2.5 text-sm text-white focus:border-ochre focus:outline-none";
  const label = "mono block text-[0.6rem] uppercase tracking-[0.16em] text-zinc-400";

  return (
    <div className="bg-black pb-24 pt-12 md:pt-16" data-testid="site-appraisal-page">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <Reveal>
          <p className="overline text-ochre">Site appraisal</p>
          <h1 className="display-1 mt-5 max-w-4xl">One Plot, One Indicative Scenario</h1>
          <p className="body-copy mt-6 max-w-3xl">{d.intro}</p>
          <NotAValuation className="mt-7 max-w-3xl" />
          <div className="mono mt-8 flex max-w-3xl items-start gap-3 border-l-2 border-maasai bg-[#0b0b0b] p-5 text-[0.6rem] uppercase leading-relaxed tracking-[0.14em] text-zinc-400">
            <FileText size={16} className="mt-0.5 shrink-0 text-maasai" />
            <span><VerifyChip v="estimate" /> {d.caveat}</span>
          </div>
        </Reveal>

        <section className="mt-14" data-testid="appraisal-controls">
          <p className="eyebrow">Your plot</p>
          <h2 className="display-3 mt-4">Describe The Land</h2>
          <div className="mt-7 flex flex-wrap items-end gap-4">
            <label className="block">
              <span className={label}>Length, m</span>
              <input value={length} onChange={(e) => setLength(e.target.value.replace(/[^\d.]/g, ""))}
                inputMode="decimal" data-testid="appraisal-length" className={input} />
            </label>
            <label className="block">
              <span className={label}>Width, m</span>
              <input value={width} onChange={(e) => setWidth(e.target.value.replace(/[^\d.]/g, ""))}
                inputMode="decimal" data-testid="appraisal-width" className={input} />
            </label>
            <label className="block">
              <span className={label}>County</span>
              <select value={county} onChange={(e) => setCounty(e.target.value)} data-testid="appraisal-county" className={select}>
                {counties.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label className="block">
              <span className={label}>Where the land is</span>
              <select value={location} onChange={(e) => setLocation(e.target.value)} data-testid="appraisal-location" className={select}>
                {LOCATIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </label>
            <label className="block">
              <span className={label}>What you would build</span>
              <select value={facility} onChange={(e) => setFacility(e.target.value)} data-testid="appraisal-facility" className={select}>
                {(facilities.length ? facilities.map((f) => [f.id, `${f.name} — ${f.tier_label}`]) : [[facility, d.facility.name]])
                  .map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </label>
            <label className="block">
              <span className={label}>How you get the land</span>
              <select value={term} onChange={(e) => setTerm(e.target.value)} data-testid="appraisal-term" className={select}>
                {TERMS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </label>
            <a href={`${BACKEND}/api/site-appraisal.pdf?${query}`} target="_blank" rel="noreferrer"
              data-testid="appraisal-pdf"
              className="mono mb-0.5 inline-flex items-center gap-2 border border-ochre px-4 py-2.5 text-[0.6rem] uppercase tracking-[0.16em] text-ochre transition-colors hover:bg-ochre hover:text-black">
              <Download size={13} /> Appraisal PDF
            </a>
            <button type="button" onClick={copyLink} data-testid="appraisal-copy-link"
              className="mono mb-0.5 inline-flex items-center gap-2 border border-zinc-700 px-4 py-2.5 text-[0.6rem] uppercase tracking-[0.16em] text-zinc-300 transition-colors hover:border-ochre hover:text-ochre">
              <Copy size={13} /> Copy link to this scenario
            </button>
            <StartAgain label="Start again" to="/site-appraisal" className="mb-0.5" testId="appraisal-start-again" />
          </div>
          <p className="mono mt-4 text-[0.6rem] uppercase tracking-[0.16em] text-zinc-400" data-testid="appraisal-plot-note">
            {plot.acres} {plot.acres === 1 ? "acre" : "acres"} · {plot.square_metres.toLocaleString()} m² · {d.location_label}
          </p>
        </section>

        <section className="mt-16" data-testid="appraisal-headline">
          <p className="eyebrow">The headline</p>
          <h2 className="display-3 mt-4">Is It Worth More Than It Costs</h2>
          <div className="mt-8 grid gap-px bg-zinc-800 sm:grid-cols-2 lg:grid-cols-5">
            {[["Land, day one", ksh(land.day_one_shillings)],
              ["Build and fees", ksh(value.build_cost_shillings + value.fees_shillings)],
              ["Total cost", ksh(value.total_cost_shillings)],
              ["Value when trading", ksh(value.gross_development_value_shillings)],
              ["Surplus on cost", `${ksh(value.surplus_shillings)} · ${value.profit_on_cost_pct}%`]]
              .map(([k, v], i) => (
                <div key={k} className="bg-[#0b0b0b] p-6" data-testid={`appraisal-stat-${i}`}>
                  <p className="mono text-[0.6rem] uppercase tracking-[0.16em] text-zinc-400">{k}</p>
                  <p className={`mono mt-3 text-sm ${i === 4 ? (value.viable ? "text-ochre" : "text-maasai") : "text-white"}`}>{v}</p>
                </div>
              ))}
          </div>
          <p className={`mt-6 max-w-3xl text-sm leading-relaxed ${value.viable ? "text-zinc-300" : "text-maasai"}`}
            data-testid="appraisal-verdict">
            {value.verdict}
          </p>
        </section>

        <section className="mt-16" data-testid="appraisal-plan">
          <p className="eyebrow">Drawn to scale</p>
          <h2 className="display-3 mt-4">What Fits On It</h2>
          <p className="mt-6 max-w-3xl text-sm leading-relaxed text-zinc-400">
            {d.facility.land_verdict} {plan.note}
          </p>
          <div className="mt-8 overflow-x-auto border border-zinc-900 bg-[#070707] p-5">
            <svg width={plot.width_m * scale} height={plot.length_m * scale}
              viewBox={`0 0 ${plot.width_m} ${plot.length_m}`} role="img"
              aria-label="Site plan" data-testid="appraisal-plan-svg">
              <rect x="0" y="0" width={plot.width_m} height={plot.length_m} fill="#120f0a" stroke="#3a3a3a" strokeWidth="0.6" />
              {plan.rects.map((r, i) => (
                <rect key={i} x={r.x} y={r.y} width={r.w} height={r.h} fill="rgba(63,164,91,0.35)"
                  stroke="#3FA45B" strokeWidth="0.5" />
              ))}
            </svg>
          </div>
          <p className="mono mt-4 text-[0.6rem] uppercase tracking-[0.16em] text-ochre" data-testid="appraisal-plan-note">
            {plan.count === 0
              ? `No pitch fits this plot — ${plan.plot_area_m2.toLocaleString()} m² is not enough for the smallest format with its run-off`
              : `${plan.count} x ${plan.format_name.toLowerCase()} in a ${plan.rows} x ${plan.cols} grid · ${plan.spare_m2.toLocaleString()} m² left for parking, changing rooms and access`}
          </p>

          <div className="mt-8 overflow-x-auto border border-zinc-900">
            <table className="w-full min-w-[760px] text-left" data-testid="appraisal-fits-table">
              <thead>
                <tr className="border-b border-zinc-800 bg-[#0b0b0b]">
                  {["Format", "Playing size", "Fenced footprint", "Fits on this plot", "Grid"].map((h) => (
                    <th key={h} className="mono px-4 py-3 text-[0.6rem] uppercase tracking-[0.16em] text-zinc-300">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {d.fits.map((f) => (
                  <tr key={f.format} className="border-b border-zinc-900" data-testid={`appraisal-fit-${f.format}`}>
                    <td className="px-4 py-4 font-display text-sm uppercase text-white">{f.name}</td>
                    <td className="mono px-4 py-4 text-xs text-zinc-300">{f.playing}</td>
                    <td className="mono px-4 py-4 text-xs text-zinc-300">{f.fenced}</td>
                    <td className="mono px-4 py-4 text-sm text-ochre">{f.fits}</td>
                    <td className="mono px-4 py-4 text-xs text-zinc-400">{f.grid}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="mt-16 grid gap-8 lg:grid-cols-2" data-testid="appraisal-numbers">
          <div>
            <p className="eyebrow">The land</p>
            <h2 className="display-3 mt-4">Every Route Priced</h2>
            <div className="mt-7 space-y-px bg-zinc-800">
              {land.routes.map((r) => (
                <div key={r.term} className={`flex items-baseline justify-between gap-4 bg-[#0b0b0b] px-5 py-4 ${r.term === land.term ? "border-l-2 border-ochre" : ""}`}
                  data-testid={`appraisal-route-${r.term}`}>
                  <span className="font-display text-sm uppercase text-white">{r.term_label}</span>
                  <span className="mono text-xs text-zinc-300">
                    {r.day_one_shillings ? ksh(r.day_one_shillings) : "Nothing"}
                    {r.rent_year_shillings ? <span className="text-zinc-400"> · {ksh(r.rent_year_shillings)}/yr</span> : null}
                  </span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <p className="eyebrow">The business</p>
            <h2 className="display-3 mt-4">Build, Trade, Pay The Rent</h2>
            <div className="mt-7 space-y-px bg-zinc-800">
              {[["Build and fees", ksh(value.build_cost_shillings + value.fees_shillings)],
                ["Net income a year", ksh(trading.income_shillings)],
                ["Operating profit", `${ksh(trading.ebitda_shillings)} · ${trading.ebitda_margin_pct}%`],
                ["Payback period (years)", trading.payback_years ? `${trading.payback_years} years`
                  : `Never on trading alone — gap of ${ksh(trading.annual_gap_shillings)} a year`],
                ["Operating surplus, share of build cost", trading.roce_pct ? `${trading.roce_pct}%` : "—"],
                ["Market ground rent", `${ksh(rent.market_rent_year_shillings)} · ${rent.share_of_income_pct}% of income`],
                ["The rent test", rent.verdict]].map(([k, v], i) => (
                <div key={k} className="flex items-baseline justify-between gap-4 bg-[#0b0b0b] px-5 py-4"
                  data-testid={`appraisal-line-${i}`}>
                  <span className="mono text-[0.6rem] uppercase tracking-[0.16em] text-zinc-400">{k}</span>
                  <span className={`mono text-xs ${i === 6 && !rent.carries_the_rent ? "text-maasai" : "text-white"}`}>{v}</span>
                </div>
              ))}
            </div>
            <p className="mt-5 text-sm leading-relaxed text-zinc-500">{rent.verdict_note}</p>
          </div>
        </section>

        <section className="mt-16 max-w-3xl" data-testid="appraisal-method">
          <p className="eyebrow">How this is worked out</p>
          <h2 className="display-3 mt-4">The Method, Attached</h2>
          <ul className="mt-6 space-y-3">
            {d.method.map((m) => (
              <li key={m} className="border-l-2 border-maasai pl-4 text-sm leading-relaxed text-zinc-400">{m}</li>
            ))}
          </ul>
          <div className="mono mt-8 flex flex-wrap gap-4 text-[0.6rem] uppercase tracking-[0.16em]">
            <Link to="/lease-comparison" className="text-ochre hover:text-white" data-testid="appraisal-to-lease">Compare land routes</Link>
            <Link to="/facility-planner" className="text-ochre hover:text-white" data-testid="appraisal-to-planner">Facility planner</Link>
            <Link to="/apply" className="text-ochre hover:text-white" data-testid="appraisal-to-apply">File this project</Link>
          </div>
        </section>
      </div>
    </div>
  );
}
